import CustomTooltip from '../../../shared/CustomTooltip';
import {
    Button,
    Grid,
    InputLabel,
    List,
    ListItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import UserRole from '../../../../shared/AuthRoles';
import { useGetCompanyOwnerInfoQuery } from '../../../../app/Slices/SevenDayApiSlice';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BusinessIcon from '@mui/icons-material/Business';
import React, { useEffect, useMemo } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import FormTextField from '../../../shared/FormTextField';
import FormSelect from '../../../shared/FormSelect';
import FormCheckbox from '../../../shared/FormCheckbox';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import ProfileSectionCard from '../ProfileSectionCard';
import ProfileReadOnlyField, {
    companyCardAccessLabel,
    formatCompanyAddressSingleLine,
    ProfileDisabledFieldWrap,
    ProfileShellFormCell,
    ProfileStatusDot,
    ProfileStatusIndicator,
} from '../ProfileReadOnlyField';
import { useEditProfileNavigation } from '../useEditProfileNavigation';
import { useProfileInlineUx } from '../ProfileInlineUxContext';
import { NavigateNext } from '@mui/icons-material';
import { Link } from 'react-router';

interface OwnerCompanyDetailsProps {
    isEditing: boolean;
    modernProfileView?: boolean;
    contractorCompany?: boolean;
}

export default function OwnerCompanyDetails({
    isEditing,
    modernProfileView = false,
    contractorCompany = false,
}: OwnerCompanyDetailsProps) {
    const { control, reset, getValues } = useFormContext();
    const { isDirty } = useFormState({ control });
    const loggedInUser = UserRole();
    const { data: getCompanyDetails } = useGetCompanyOwnerInfoQuery(
        loggedInUser?.company,
    );

    const companyW = useWatch({ control, name: 'company' }) as
        | Record<string, any>
        | undefined;

    const mergedCompany = useMemo(
        () => ({
            ...(getCompanyDetails && typeof getCompanyDetails === 'object'
                ? getCompanyDetails
                : {}),
            ...(companyW && typeof companyW === 'object' ? companyW : {}),
        }),
        [getCompanyDetails, companyW],
    );

    const { canShowEdit } = useEditProfileNavigation(contractorCompany);
    /** owner_company may edit; owner_admin / owner_user / view-only may not. */
    const canShowCompanyInlineEdit =
        canShowEdit &&
        loggedInUser?.role !== 'owner_admin' &&
        loggedInUser?.role !== 'owner_user' &&
        !loggedInUser?.viewOnly;
    const {
        suspendCompanyResets,
        inlineEditSection,
        beginInlineEdit,
        cancelInlineEdit,
        saveInlineEdit,
    } = useProfileInlineUx();

    useEffect(() => {
        if (suspendCompanyResets) return;
        reset({ ...getValues(), company: getCompanyDetails });
    }, [getCompanyDetails, suspendCompanyResets, reset, getValues]);

    const isCompany = loggedInUser?.role === 'owner_company';

    const simplifiedAddress = `${getCompanyDetails?.streetAddress} ${getCompanyDetails?.suburb} ${getCompanyDetails?.postcode} ${getCompanyDetails?.state} ${getCompanyDetails?.countryCode}`;

    /** Card chrome for modern profile page; must not depend on `isEditing` or layout jumps when inline-editing */
    const shell = Boolean(modernProfileView);
    const readOnlyShell = shell && !isEditing;

    const ownerCompanyReadOnly = (
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ rowGap: 0.5 }}>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Company'>
                    {mergedCompany?.name}
                </ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Address'>
                    {formatCompanyAddressSingleLine(mergedCompany)}
                </ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Phone'>
                    {mergedCompany?.phone}
                </ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Primary Contact'>
                    {mergedCompany?.primaryContact}
                </ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Email'>
                    {mergedCompany?.account?.email}
                </ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Notification Email'>
                    {mergedCompany?.notificationsEmail}
                </ProfileReadOnlyField>
            </Grid>
            {(loggedInUser?.role === 'owner_company' ||
                loggedInUser?.role === 'guest_company') && (
                <>
                    <Grid item xs={12} sm={6} md={4}>
                        <ProfileReadOnlyField
                            label='Status'
                            labelAdornment={
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            <List>
                                                <ListItem>
                                                    Enabled: Users can access
                                                    the site and perform actions
                                                    based on their level of
                                                    access
                                                </ListItem>
                                                <ListItem>
                                                    Disabled: Users will not be
                                                    able to access the site
                                                </ListItem>
                                            </List>
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                            }
                        >
                            <ProfileStatusIndicator
                                active={mergedCompany?.isActive}
                            />
                        </ProfileReadOnlyField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <ProfileReadOnlyField
                            label='Access'
                            labelAdornment={
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            <List>
                                                <ListItem>
                                                    Master Account:
                                                    Add/Edit/View Users
                                                </ListItem>
                                                <ListItem>
                                                    Admin: Add/Edit/View
                                                    Listings, Users and
                                                    Reservations
                                                </ListItem>
                                                <ListItem>
                                                    User: Add/Edit/View
                                                    Reservations for themselves
                                                    & view listings
                                                </ListItem>
                                            </List>
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{ fontSize: '16px' }}
                                    />
                                </CustomTooltip>
                            }
                        >
                            {companyCardAccessLabel(loggedInUser)}
                        </ProfileReadOnlyField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <ProfileReadOnlyField
                            label='Allow multiple domains'
                            labelAdornment={
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            <List>
                                                <ListItem>
                                                    If checked, users will be
                                                    allowed to add email
                                                    addresses that don&apos;t
                                                    belong to the organsation to
                                                    access 7DAY such as personal
                                                    emails. If unchecked, users
                                                    cannot add an email address
                                                    that is outside the{' '}
                                                    {getCompanyDetails?.name}{' '}
                                                    organisation.
                                                </ListItem>
                                            </List>
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                            }
                        >
                            {mergedCompany?.allowsMultipleDomains
                                ? 'Yes'
                                : 'No'}
                        </ProfileReadOnlyField>
                    </Grid>
                </>
            )}
            <Grid item xs={12} sx={{ display: 'flex' }}>
                <Button
                    component={Link}
                    to={`/hosts/${loggedInUser?.company}`}
                    endIcon={<NavigateNext />}
                    sx={{
                        '&:hover': {
                            color: 'inherit',
                        },
                        ml: 'auto',
                    }}
                >
                    View company profile
                </Button>
            </Grid>
        </Grid>
    );

    const ownerCompanyShellEdit = (
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ rowGap: 0.5 }}>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell label='Company'>
                    <ProfileDisabledFieldWrap disabled>
                        <FormTextField
                            name='company.name'
                            control={control}
                            disabled
                            size='small'
                            fullWidth
                        />
                    </ProfileDisabledFieldWrap>
                </ProfileShellFormCell>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell label='Address'>
                    {isEditing && isCompany ? (
                        <Stack spacing={1}>
                            <FormTextField
                                name='company.streetAddress'
                                control={control}
                                size='small'
                                fullWidth
                            />
                            <FormTextField
                                name='company.suburb'
                                control={control}
                                size='small'
                                fullWidth
                            />
                            <FormTextField
                                type='number'
                                name='company.postcode'
                                control={control}
                                size='small'
                                fullWidth
                            />
                            <FormAutocomplete
                                name='company.state'
                                control={control}
                                options={[
                                    { value: 'NSW', label: 'NSW' },
                                    { value: 'QLD', label: 'QLD' },
                                    { value: 'VIC', label: 'VIC' },
                                    { value: 'SA', label: 'SA' },
                                    { value: 'WA', label: 'WA' },
                                    { value: 'NT', label: 'NT' },
                                    { value: 'TAS', label: 'TAS' },
                                    { value: 'ACT', label: 'ACT' },
                                ]}
                            />
                            <FormAutocomplete
                                name='company.countryCode'
                                control={control}
                                options={[{ value: 'AU', label: 'Australia' }]}
                            />
                        </Stack>
                    ) : (
                        <ProfileDisabledFieldWrap
                            disabled={!(isEditing && isCompany)}
                        >
                            <TextField
                                size='small'
                                fullWidth
                                disabled
                                value={
                                    formatCompanyAddressSingleLine(
                                        mergedCompany,
                                    ) || '—'
                                }
                            />
                        </ProfileDisabledFieldWrap>
                    )}
                </ProfileShellFormCell>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell label='Phone'>
                    <ProfileDisabledFieldWrap
                        disabled={!isEditing || !isCompany}
                    >
                        <FormTextField
                            name='company.phone'
                            control={control}
                            disabled={!isEditing || !isCompany}
                            size='small'
                            fullWidth
                        />
                    </ProfileDisabledFieldWrap>
                </ProfileShellFormCell>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell label='Primary Contact'>
                    <ProfileDisabledFieldWrap
                        disabled={!isEditing || !isCompany}
                    >
                        <FormTextField
                            name='company.primaryContact'
                            control={control}
                            disabled={!isEditing || !isCompany}
                            size='small'
                            fullWidth
                        />
                    </ProfileDisabledFieldWrap>
                </ProfileShellFormCell>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell label='Email'>
                    <ProfileDisabledFieldWrap disabled>
                        <FormTextField
                            name='company.account.email'
                            control={control}
                            disabled
                            size='small'
                            fullWidth
                        />
                    </ProfileDisabledFieldWrap>
                </ProfileShellFormCell>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell label='Notification Email'>
                    <ProfileDisabledFieldWrap
                        disabled={!isEditing || !isCompany}
                    >
                        <FormTextField
                            name='company.notificationsEmail'
                            control={control}
                            disabled={!isEditing || !isCompany}
                            size='small'
                            fullWidth
                        />
                    </ProfileDisabledFieldWrap>
                </ProfileShellFormCell>
            </Grid>
            {(loggedInUser?.role === 'owner_company' ||
                loggedInUser?.role === 'guest_company') && (
                <>
                    <Grid item xs={12} sm={6} md={4}>
                        <ProfileShellFormCell
                            label='Status'
                            labelAdornment={
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            <List>
                                                <ListItem>
                                                    Enabled: Users can access
                                                    the site and perform actions
                                                    based on their level of
                                                    access
                                                </ListItem>
                                                <ListItem>
                                                    Disabled: Users will not be
                                                    able to access the site
                                                </ListItem>
                                            </List>
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                            }
                        >
                            <ProfileDisabledFieldWrap disabled>
                                <FormSelect
                                    name='company.isActive'
                                    control={control}
                                    size='small'
                                    fullWidth
                                    options={[
                                        {
                                            value: true,
                                            label: 'Enabled',
                                            iconLeft: (
                                                <ProfileStatusDot enabled />
                                            ),
                                        },
                                        {
                                            value: false,
                                            label: 'Disabled',
                                            iconLeft: (
                                                <ProfileStatusDot
                                                    enabled={false}
                                                />
                                            ),
                                        },
                                    ]}
                                    disabled
                                />
                            </ProfileDisabledFieldWrap>
                        </ProfileShellFormCell>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <ProfileShellFormCell
                            label='Access'
                            labelAdornment={
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            <List>
                                                <ListItem>
                                                    Master Account:
                                                    Add/Edit/View Users
                                                </ListItem>
                                                <ListItem>
                                                    Admin: Add/Edit/View
                                                    Listings, Users and
                                                    Reservations
                                                </ListItem>
                                                <ListItem>
                                                    User: Add/Edit/View
                                                    Reservations for themselves
                                                    & view listings
                                                </ListItem>
                                            </List>
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{ fontSize: '16px' }}
                                    />
                                </CustomTooltip>
                            }
                        >
                            <TextField
                                size='small'
                                fullWidth
                                disabled
                                value={companyCardAccessLabel(loggedInUser)}
                            />
                        </ProfileShellFormCell>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <ProfileShellFormCell
                            label='Allow multiple domains'
                            labelAdornment={
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            <List>
                                                <ListItem>
                                                    If checked, users will be
                                                    allowed to add email
                                                    addresses that don&apos;t
                                                    belong to the organsation to
                                                    access 7DAY such as personal
                                                    emails. If unchecked, users
                                                    cannot add an email address
                                                    that is outside the{' '}
                                                    {getCompanyDetails?.name}{' '}
                                                    organisation.
                                                </ListItem>
                                            </List>
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                            }
                        >
                            <ProfileDisabledFieldWrap
                                disabled={!isEditing || !isCompany}
                            >
                                <FormCheckbox
                                    name='company.allowsMultipleDomains'
                                    control={control}
                                    disabled={!isEditing || !isCompany}
                                />
                            </ProfileDisabledFieldWrap>
                        </ProfileShellFormCell>
                    </Grid>
                </>
            )}
        </Grid>
    );

    const companyFieldsGrid = (
        <Grid container columnSpacing='50px' rowSpacing='10px'>
            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <InputLabel>Company</InputLabel>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <FormTextField
                        name='company.name'
                        control={control}
                        // disabled={!isEditing || !isCompany}
                        disabled
                    />
                </Grid>
            </Grid>
            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <InputLabel>Phone</InputLabel>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <FormTextField
                        name='company.phone'
                        control={control}
                        disabled={!isEditing || !isCompany}
                    />
                </Grid>
            </Grid>
            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <InputLabel>Primary Contact</InputLabel>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <FormTextField
                        name='company.primaryContact'
                        control={control}
                        disabled={!isEditing || !isCompany}
                    />
                </Grid>
            </Grid>
            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <InputLabel>Email</InputLabel>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <FormTextField
                        name='company.account.email'
                        control={control}
                        // disabled={!isEditing || !isCompany}
                        disabled
                    />
                </Grid>
            </Grid>
            {!isEditing && (
                <Grid item container xs={12} md={6}>
                    <Grid item xs={12} lg={5}>
                        <InputLabel>Address</InputLabel>
                    </Grid>
                    <Grid item xs={12} lg={7}>
                        <TextField
                            size='small'
                            fullWidth
                            value={simplifiedAddress}
                            disabled={!isEditing || !isCompany}
                        />
                    </Grid>
                </Grid>
            )}
            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <InputLabel>Notification Email</InputLabel>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <FormTextField
                        name='company.notificationsEmail'
                        control={control}
                        disabled={!isEditing || !isCompany}
                    />
                </Grid>
            </Grid>
            {isEditing && loggedInUser?.role === 'owner_company' && (
                <>
                    <Grid item xs={12} />
                    <Grid item container xs={12} md={6}>
                        <Grid item xs={12} lg={5}>
                            <InputLabel>Street Address</InputLabel>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <FormTextField
                                name='company.streetAddress'
                                control={control}
                            />
                        </Grid>
                    </Grid>
                    <Grid item container xs={12} md={6}>
                        <Grid item xs={12} lg={5}>
                            <InputLabel>Suburb</InputLabel>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <FormTextField
                                name='company.suburb'
                                control={control}
                            />
                        </Grid>
                    </Grid>
                    <Grid item container xs={12} md={6}>
                        <Grid item xs={12} lg={5}>
                            <InputLabel>Post Code</InputLabel>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <FormTextField
                                type='number'
                                name='company.postcode'
                                control={control}
                            />
                        </Grid>
                    </Grid>
                    <Grid item container xs={12} md={6}>
                        <Grid item xs={12} lg={5}>
                            <InputLabel>State</InputLabel>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <FormAutocomplete
                                name='company.state'
                                control={control}
                                options={[
                                    { value: 'NSW', label: 'NSW' },
                                    { value: 'QLD', label: 'QLD' },
                                    { value: 'VIC', label: 'VIC' },
                                    { value: 'SA', label: 'SA' },
                                    { value: 'WA', label: 'WA' },
                                    { value: 'NT', label: 'NT' },
                                    { value: 'TAS', label: 'TAS' },
                                    { value: 'ACT', label: 'ACT' },
                                ]}
                            />
                        </Grid>
                    </Grid>
                    <Grid container item xs={12} lg={6}>
                        <Grid item xs={12} lg={5}>
                            <InputLabel required>Country</InputLabel>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <FormAutocomplete
                                name='company.countryCode'
                                control={control}
                                options={[{ value: 'AU', label: 'Australia' }]}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12} />
                </>
            )}
            {loggedInUser?.role === 'owner_company' ||
            loggedInUser?.role === 'guest_company' ? (
                <>
                    <Grid item container xs={12} md={6}>
                        <Grid item xs={12} lg={5}>
                            <Stack
                                direction='row'
                                gap='5px'
                                alignItems='center'
                            >
                                <InputLabel>Status</InputLabel>
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            <List>
                                                <ListItem>
                                                    {' '}
                                                    Enabled: Users can access
                                                    the site and perform actions
                                                    based on their level of
                                                    access
                                                </ListItem>
                                                <ListItem>
                                                    {' '}
                                                    Disabled: Users will not be
                                                    able to access the site
                                                </ListItem>
                                            </List>
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <FormSelect
                                name='company.isActive'
                                control={control}
                                options={[
                                    {
                                        value: true,
                                        label: 'Enabled',
                                        iconLeft: <ProfileStatusDot enabled />,
                                    },
                                    {
                                        value: false,
                                        label: 'Disabled',
                                        iconLeft: (
                                            <ProfileStatusDot enabled={false} />
                                        ),
                                    },
                                ]}
                                disabled
                            />
                        </Grid>
                    </Grid>
                    <Grid item container xs={12} md={6}>
                        <Grid item xs={12} lg={5}>
                            <Stack
                                direction='row'
                                gap='5px'
                                alignItems='center'
                            >
                                <InputLabel>Access</InputLabel>
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            <List>
                                                <ListItem>
                                                    {' '}
                                                    Master Account:
                                                    Add/Edit/View Users
                                                </ListItem>
                                                <ListItem>
                                                    {' '}
                                                    Admin: Add/Edit/View
                                                    Listings, Users and
                                                    Reservations
                                                </ListItem>
                                                <ListItem>
                                                    {' '}
                                                    User: Add/Edit/View
                                                    Reservations for themselves
                                                    & view listings
                                                </ListItem>
                                            </List>
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <TextField
                                size='small'
                                fullWidth
                                value={
                                    loggedInUser?.role === 'owner_admin'
                                        ? 'Admin'
                                        : loggedInUser?.role === 'owner_user'
                                          ? 'User'
                                          : loggedInUser?.role === 'guest_user'
                                            ? loggedInUser?.viewOnly
                                                ? 'View Only User'
                                                : 'User'
                                            : loggedInUser?.role === 'admin'
                                              ? 'Admin'
                                              : 'Master Account'
                                }
                                disabled
                            />
                        </Grid>
                    </Grid>
                    <Grid item container xs={12} md={6}>
                        <Grid item xs={12} lg={5}>
                            <Stack
                                direction='row'
                                gap='5px'
                                alignItems='center'
                            >
                                <InputLabel>Allow multiple domains</InputLabel>
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            <List>
                                                <ListItem>
                                                    {' '}
                                                    If checked, users will be
                                                    allowed to add email
                                                    addresses that don't belong
                                                    to the organsation to access
                                                    7DAY such as personal
                                                    emails. If unchecked, users
                                                    cannot add an email address
                                                    that is outside the{' '}
                                                    {
                                                        getCompanyDetails?.name
                                                    }{' '}
                                                    organisation.
                                                </ListItem>
                                            </List>
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <FormCheckbox
                                name='company.allowsMultipleDomains'
                                control={control}
                                disabled={!isEditing || !isCompany}
                            />
                        </Grid>
                    </Grid>
                </>
            ) : null}
        </Grid>
    );

    return (
        <>
            {shell ? (
                <ProfileSectionCard
                    icon={<BusinessIcon sx={{ fontSize: 22 }} />}
                    title='Company Details'
                    subtitle='Information about your company and primary contact.'
                    editButtonLabel='Edit Company'
                    onEdit={() => beginInlineEdit('company')}
                    showEditButton={canShowCompanyInlineEdit}
                    inlineEditActive={inlineEditSection === 'company'}
                    onCancelInline={cancelInlineEdit}
                    onSaveInline={saveInlineEdit}
                    saveDisabled={inlineEditSection === 'company' && !isDirty}
                >
                    {readOnlyShell
                        ? ownerCompanyReadOnly
                        : ownerCompanyShellEdit}
                </ProfileSectionCard>
            ) : (
                <>
                    <Typography
                        component='h4'
                        sx={{
                            color: 'text.primary',
                            fontWeight: 'bold',
                            py: '30px',
                        }}
                    >
                        Company Details
                    </Typography>
                    {companyFieldsGrid}
                </>
            )}
        </>
    );
}
